import { privateBaseQuery } from "@/api/base/private/privateBaseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";

import {
  CreateFaqRequest,
  FaqCategory,
  FaqEntity,
  FaqListParams,
  FaqPaginatedEntities,
  GetFaqCategoriesResponse,
  GetFaqListResponse,
  GetFaqResponse,
  UpdateFaqRequest,
} from "@/types/faq";

const FAQ_LIST_FIELDS = ["id", "faq_category_id", "question", "category"];
const FAQ_DETAIL_FIELDS = [
  "id",
  "school_id",
  "faq_category_id",
  "question_translations",
  "answer_translations",
  "category",
];

function localeHeader(locale?: string): Record<string, string> | undefined {
  if (!locale) return undefined;
  const normalized = locale === "kk" ? "kz" : locale;
  return { Locale: normalized };
}

export const faqApi = createApi({
  reducerPath: "faqApi",
  baseQuery: privateBaseQuery(),
  tagTypes: ["FaqList", "FaqDetail", "FaqCategories"],

  endpoints: (builder) => ({
    getFaqList: builder.query<FaqPaginatedEntities, FaqListParams>({
      query: ({ locale, ...params }) => ({
        url: "/faq",
        method: "GET",
        params,
        headers: {
          "X-Requested-Fields": FAQ_LIST_FIELDS.join(","),
          ...localeHeader(locale),
        },
      }),
      transformResponse: (response: GetFaqListResponse) => response.entities,
      providesTags: ["FaqList"],
    }),

    getFaq: builder.query<FaqEntity, { id: number; locale?: string }>({
      query: ({ id, locale }) => ({
        url: `/faq/${id}`,
        method: "GET",
        headers: {
          "X-Requested-Fields": FAQ_DETAIL_FIELDS.join(","),
          ...localeHeader(locale),
        },
      }),
      transformResponse: (response: GetFaqResponse) => response.entity,
      providesTags: (result, error, { id }) => [{ type: "FaqDetail", id }],
    }),

    createFaq: builder.mutation<FaqEntity, CreateFaqRequest>({
      query: (payload) => ({
        url: "/faq",
        method: "POST",
        data: payload,
        headers: { "Content-Type": "application/json" },
      }),
      transformResponse: (response: GetFaqResponse) => response.entity,
      invalidatesTags: ["FaqList"],
    }),

    updateFaq: builder.mutation<FaqEntity, UpdateFaqRequest>({
      query: ({ id, ...payload }) => ({
        url: `/faq/${id}`,
        method: "PUT",
        data: payload,
        headers: { "Content-Type": "application/json" },
      }),
      transformResponse: (response: GetFaqResponse) => response.entity,
      invalidatesTags: (result, error, { id }) => [
        { type: "FaqDetail", id },
        "FaqList",
      ],
    }),

    deleteFaq: builder.mutation<void, number>({
      query: (id) => ({
        url: `/faq/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "FaqDetail", id },
        "FaqList",
      ],
    }),

    getFaqCategories: builder.query<FaqCategory[], { locale?: string } | void>({
      query: (arg) => ({
        url: "/faq-categories.json",
        method: "GET",
        headers: localeHeader(arg?.locale),
      }),
      transformResponse: (response: GetFaqCategoriesResponse) =>
        response.entities,
      providesTags: ["FaqCategories"],
    }),
  }),
});

export const {
  useGetFaqListQuery,
  useGetFaqQuery,
  useCreateFaqMutation,
  useUpdateFaqMutation,
  useDeleteFaqMutation,
  useGetFaqCategoriesQuery,
} = faqApi;
